import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowLeft, Search } from 'lucide-react';
import CustomButton from '@/components/atoms/CustomButton';
import FilterPill from '@/modules/content/feed/filters/parts/FilterPill';
import { domainGlyph } from '@/modules/content/feed/filters/parts/domainGlyph';
import useContentFilterOptions from '@/hooks/content/useContentFilterOptions';
import { toggleInSet } from '@/modules/content/feed/filters/model/types';
import { THealthDomain } from '@/types/Health.types';

type Props = {
  selected: string[];
  onChange: (topics: string[]) => void;
  onBack: () => void;
};

// The full topic catalog, reached by "Show All Topics". Its own search narrows the list;
// selections write straight into the shared draft, so "Apply" here just returns to the
// main sheet (final application is that sheet's "Apply Filters").
const AllTopicsView = ({ selected, onChange, onBack }: Props) => {
  const [query, setQuery] = useState('');
  // includeAllTopics=true pulls the grouped catalog.
  const { allTopicGroups, isLoading } = useContentFilterOptions(true);

  const topics = useMemo(
    () =>
      allTopicGroups.flatMap((group) =>
        group.topics.map((topic) => ({ ...topic, domain: topic.domain ?? group.domain })),
      ),
    [allTopicGroups],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter((topic) => topic.label.toLowerCase().includes(q));
  }, [topics, query]);

  return (
    <VStack align="stretch" gap="20px" height="100%">
      <HStack position="relative" justify="center">
        <IconButton
          aria-label="Back"
          icon={<ArrowLeft size={20} />}
          variant="ghost"
          borderRadius="full"
          position="absolute"
          left={0}
          onClick={onBack}
        />
        <Text variant="loraMediumTitle" color="text.darkBlue">
          All Topics
        </Text>
      </HStack>

      <HStack w={'100%'} pl={2}>
        <InputGroup size="lg">
          <InputLeftElement pointerEvents="none" color="text.mediumBlue">
            <Search size={18} />
          </InputLeftElement>
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            backgroundColor="white"
            borderRadius="14px"
            aria-label="Search topics"
          />
        </InputGroup>
      </HStack>

      <Box flex="1" overflowY="auto" minHeight="200px">
        {isLoading ? (
          <HStack justify="center" paddingY="60px">
            <Spinner color="primary.500" size="lg" />
          </HStack>
        ) : filtered.length === 0 ? (
          <Text fontSize="14px" color="text.mediumBlue" textAlign="center" paddingY="40px">
            No topics match “{query}”.
          </Text>
        ) : (
          <Flex wrap="wrap" gap="10px" p={2}>
            {filtered.map((topic) => (
              <FilterPill
                key={topic.value}
                label={topic.label}
                icon={topic.domain ? domainGlyph(topic.domain as THealthDomain) : undefined}
                selected={selected.includes(topic.value)}
                onClick={() => onChange(toggleInSet(selected, topic.value))}
              />
            ))}
          </Flex>
        )}
      </Box>

      <CustomButton
        label="Apply"
        width="100%"
        backgroundColor="secondary.500"
        labelColor="white"
        hoverBackgroundColor="secondary.600"
        onClick={onBack}
      />
    </VStack>
  );
};

export default AllTopicsView;
